class CustomVideo extends HTMLElement {
  constructor() {
    super();
    this._isInitialized = false;
    this._initTimeoutId = null;
    this._cleanup = [];
  }

  connectedCallback() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    // Wait for Light DOM children to be parsed
    this._initTimeoutId = window.setTimeout(() => {
      const wrapper = this;
      const video = wrapper.querySelector('video');
      const playBtn = wrapper.querySelector('.play-pause');
      const centralPlayBtn = wrapper.querySelector('[data-hero-card-play-pause]');
      const progress = wrapper.querySelector('.progress');
      const muteBtn = wrapper.querySelector('.mute');
      const fullscreenBtn = wrapper.querySelector('.fullscreen');
      const timeDisplay = wrapper.querySelector('.time-display');
      const timeDisplayText = wrapper.querySelector('.time-display-text');

      if (!video) return;

      // 🔥 Programmatic autoplay fallback (muted videos are allowed by browsers)
      video.play?.().catch(() => {});

      // 🔥 Prevent video from taking focus
      video.setAttribute('tabindex', '-1');

      let liveRegion = wrapper.querySelector('.sr-only');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.className = 'sr-only';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        wrapper.appendChild(liveRegion);
      }

      function formatTime(time) {
        if (isNaN(time)) return '0:00';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60)
          .toString()
          .padStart(2, '0');
        return `${m}:${s}`;
      }

      function formatSpokenTime(time) {
        if (isNaN(time)) return '0 seconds';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        if (m > 0) {
          return `${m} minute${m !== 1 ? 's' : ''} ${s} second${s !== 1 ? 's' : ''}`;
        }
        return `${s} second${s !== 1 ? 's' : ''}`;
      }

      let duration = video.duration && !isNaN(video.duration) ? video.duration : 0;

      const onLoadedMetadata = () => {
        duration = video.duration;
        if (progress) progress.setAttribute('aria-valuemax', Math.floor(duration));
        if (timeDisplay && timeDisplayText) {
          timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
          timeDisplay.setAttribute(
            'aria-label',
            `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`,
          );
        }
      };
      video.addEventListener('loadedmetadata', onLoadedMetadata);
      this._cleanup.push(() => video.removeEventListener('loadedmetadata', onLoadedMetadata));

      if (duration > 0 && timeDisplay && timeDisplayText) {
        timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
        timeDisplay.setAttribute(
          'aria-label',
          `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`,
        );
      }

      const togglePlay = () => {
        video.paused ? video.play() : video.pause();
      };

      if (playBtn) {
        const onPlayBtnClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        };
        playBtn.addEventListener('click', onPlayBtnClick);
        this._cleanup.push(() => playBtn.removeEventListener('click', onPlayBtnClick));
      }

      if (centralPlayBtn) {
        const onCentralPlayClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        };
        centralPlayBtn.addEventListener('click', onCentralPlayClick);
        this._cleanup.push(() => centralPlayBtn.removeEventListener('click', onCentralPlayClick));
      }

      const updateCentralPlayBtnUI = (paused) => {
        wrapper.classList.toggle('is-paused', paused);
        if (!centralPlayBtn) return;
        const playIcon = centralPlayBtn.querySelector('[data-hero-card-icon="play"]');
        const pauseIcon = centralPlayBtn.querySelector('[data-hero-card-icon="pause"]');
        const initialLabel = centralPlayBtn.getAttribute('aria-label') || 'Play';
        const labelDesc = initialLabel.replace(/^(Play|Pause)\s+/i, '');

        centralPlayBtn.setAttribute('aria-label', (paused ? 'Play ' : 'Pause ') + labelDesc);

        if (playIcon) playIcon.hidden = !paused;
        if (pauseIcon) pauseIcon.hidden = paused;
      };

      // 🔥 Keyboard fix
      wrapper.querySelectorAll('button').forEach((btn) => {
        const onButtonKeydown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            btn.click();
          }
        };
        btn.addEventListener('keydown', onButtonKeydown);
        this._cleanup.push(() => btn.removeEventListener('keydown', onButtonKeydown));
      });

      // ✅ Visual update only (NO aria updates)
      const onTimeUpdate = () => {
        duration = video.duration && !isNaN(video.duration) ? video.duration : duration;

        if (!isNaN(duration) && duration > 0) {
          const percent = (video.currentTime / duration) * 100;

          if (progress) {
            progress.style.setProperty('--progress', percent + '%');

            // ✅ FIX: keep aria in sync
            progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
            progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));
          }

          if (timeDisplay && timeDisplayText) {
            timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
            timeDisplay.setAttribute(
              'aria-label',
              `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`,
            );
          }
        }
      };
      video.addEventListener('timeupdate', onTimeUpdate);
      this._cleanup.push(() => video.removeEventListener('timeupdate', onTimeUpdate));

      if (progress) {
        const onProgressFocusin = () => {
          progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));
          progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
        };
        progress.addEventListener('focusin', onProgressFocusin);
        this._cleanup.push(() => progress.removeEventListener('focusin', onProgressFocusin));

        const onProgressKeydown = (e) => {
          const step = 5;
          if (!duration || isNaN(duration)) return;

          if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();

            if (e.key === 'ArrowRight') {
              video.currentTime = Math.min(video.currentTime + step, duration);
            }

            if (e.key === 'ArrowLeft') {
              video.currentTime = Math.max(video.currentTime - step, 0);
            }

            progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
            progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));

            const currentSpoken = formatSpokenTime(video.currentTime);
            const totalSpoken = formatSpokenTime(duration);

            liveRegion.textContent = `Time ${currentSpoken} of ${totalSpoken}`;
          }
        };
        progress.addEventListener('keydown', onProgressKeydown);
        this._cleanup.push(() => progress.removeEventListener('keydown', onProgressKeydown));

        const onProgressClick = (e) => {
          if (!duration || isNaN(duration)) return;
          const rect = progress.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const width = rect.width;
          if (!width) return;

          const percent = clickX / width;
          const newTime = percent * duration;

          video.currentTime = newTime;

          // update UI
          progress.style.setProperty('--progress', percent * 100 + '%');

          // update aria ONLY on user interaction
          progress.setAttribute('aria-valuenow', Math.floor(newTime));
          progress.setAttribute('aria-valuetext', formatSpokenTime(newTime));

          // announce (optional but good)
          const currentSpoken = formatSpokenTime(newTime);
          const totalSpoken = formatSpokenTime(duration);

          liveRegion.textContent = `Time ${currentSpoken} of ${totalSpoken}`;

          // focus for accessibility (Safari fix)
          progress.focus();
        };
        progress.addEventListener('click', onProgressClick);
        this._cleanup.push(() => progress.removeEventListener('click', onProgressClick));
      }

      if (muteBtn) {
        // 🔊 Mute
        const onMuteClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          muteBtn.setAttribute('data-mute', video.muted);
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        };
        muteBtn.addEventListener('click', onMuteClick);
        this._cleanup.push(() => muteBtn.removeEventListener('click', onMuteClick));
      }

      if (fullscreenBtn) {
        // iOS Safari doesn't allow requestFullscreen on custom elements.
        // Use CSS "fake fullscreen" on iOS so custom controls remain visible.
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        let iosFakeFullscreen = false;

        const updateFullscreenUI = (inFullscreen) => {
          fullscreenBtn.setAttribute('aria-label', inFullscreen ? 'Exit fullscreen' : 'Fullscreen');
          fullscreenBtn.setAttribute('data-video-maximize', inFullscreen ? 'false' : 'true');
        };

        const enterFullscreen = () => {
          if (isIOS) {
            // ✅ Use native iOS video fullscreen
            if (video.webkitEnterFullscreen) {
              video.webkitEnterFullscreen();
            } else {
              // fallback (fake fullscreen)
              iosFakeFullscreen = true;
              wrapper.classList.add('ios-fake-fullscreen');
            }
            updateFullscreenUI(true);
            requestAnimationFrame(() => fullscreenBtn.focus());
          } else {
            (wrapper.requestFullscreen || wrapper.webkitRequestFullscreen)?.call(wrapper);
          }
        };

        const exitFullscreen = () => {
          if (isIOS) {
            iosFakeFullscreen = false;
            wrapper.classList.remove('ios-fake-fullscreen');
            updateFullscreenUI(false);
            requestAnimationFrame(() => fullscreenBtn.focus());
          } else {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
          }
        };

        const isInFullscreen = () =>
          isIOS
            ? iosFakeFullscreen
            : !!(document.fullscreenElement || document.webkitFullscreenElement);

        const onFullscreenClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          isInFullscreen() ? exitFullscreen() : enterFullscreen();
        };
        fullscreenBtn.addEventListener('click', onFullscreenClick);
        this._cleanup.push(() => fullscreenBtn.removeEventListener('click', onFullscreenClick));

        // Sync button state when real fullscreen exits via Escape key or browser UI (non-iOS)
        if (!isIOS) {
          const onFullscreenChange = () => {
            updateFullscreenUI(isInFullscreen());
            // Restore focus to fullscreen button after transition (macOS Safari drops focus)
            requestAnimationFrame(() => fullscreenBtn.focus());
          };
          document.addEventListener('fullscreenchange', onFullscreenChange);
          document.addEventListener('webkitfullscreenchange', onFullscreenChange);
          this._cleanup.push(() => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
          });
        }
      }

      // AUTO PAUSE: only when pause-after-seconds is set on <custom-video> or hero-media-card.
      // Default is continuous looped autoplay (no timed pause).
      let pauseAfterSecondsAttr = wrapper.getAttribute('pause-after-seconds');
      if (!pauseAfterSecondsAttr) {
        const heroCard = wrapper.closest('hero-media-card');
        if (heroCard) {
          pauseAfterSecondsAttr = heroCard.getAttribute('pause-after-seconds');
        }
      }
      const parsedPauseAfterSeconds = pauseAfterSecondsAttr
        ? parseInt(pauseAfterSecondsAttr, 10)
        : 0;
      const pauseAfterSeconds =
        Number.isFinite(parsedPauseAfterSeconds) && parsedPauseAfterSeconds > 0
          ? parsedPauseAfterSeconds
          : 0;

      let autoPauseListener = null;
      let hasAutoPausedOnce = false;

      const clearAutoPause = () => {
        if (autoPauseListener) {
          video.removeEventListener('timeupdate', autoPauseListener);
          autoPauseListener = null;
        }
      };

      const scheduleAutoPause = () => {
        if (pauseAfterSeconds <= 0 || hasAutoPausedOnce) return;
        clearAutoPause();
        autoPauseListener = () => {
          if (video.currentTime >= pauseAfterSeconds) {
            hasAutoPausedOnce = true;
            clearAutoPause();
            video.pause();
          }
        };
        video.addEventListener('timeupdate', autoPauseListener);
      };

      const onVideoPlay = () => {
        if (playBtn) {
          //   playBtn.textContent = '⏸';
          playBtn.setAttribute('aria-label', 'Pause video');
          playBtn.setAttribute('data-video-play', 'false');
        }
        updateCentralPlayBtnUI(false);
        scheduleAutoPause();
      };
      video.addEventListener('play', onVideoPlay);
      this._cleanup.push(() => video.removeEventListener('play', onVideoPlay));

      const onVideoPause = () => {
        if (playBtn) {
          //   playBtn.textContent = '▶';
          playBtn.setAttribute('aria-label', 'Play video');
          playBtn.setAttribute('data-video-play', 'true');
        }
        updateCentralPlayBtnUI(true);
        clearAutoPause();
      };
      video.addEventListener('pause', onVideoPause);
      this._cleanup.push(() => video.removeEventListener('pause', onVideoPause));

      if (!video.paused) {
        scheduleAutoPause();
        updateCentralPlayBtnUI(false);
      } else {
        updateCentralPlayBtnUI(true);
      }

      // 🔥 FIXED: only video click (not wrapper)
      video.addEventListener('click', togglePlay);
      this._cleanup.push(() => video.removeEventListener('click', togglePlay));
    }, 0);
  }

  disconnectedCallback() {
    if (this._initTimeoutId) {
      window.clearTimeout(this._initTimeoutId);
      this._initTimeoutId = null;
    }

    for (const cleanupFn of this._cleanup) {
      cleanupFn();
    }

    this._cleanup = [];
    this._isInitialized = false;
  }
}

if (!customElements.get('custom-video')) {
  customElements.define('custom-video', CustomVideo);
}

const RTE_VIDEO_ROOT_SELECTOR = 'rte-content, .rte, .article-template__content';

function shouldEnhanceVideo(video) {
  if (!video || video.tagName !== 'VIDEO') return false;
  if (video.hasAttribute('data-native-video')) return false;
  if (video.closest('custom-video')) return false;
  if (!video.closest(RTE_VIDEO_ROOT_SELECTOR)) return false;
  return true;
}

function getVideoAriaLabel(video) {
  return (
    video.getAttribute('aria-label') ||
    video.getAttribute('title') ||
    video.getAttribute('data-video-label') ||
    'Video'
  );
}

function setCentralPlayAriaLabel(wrapper, label) {
  const centralBtn = wrapper.querySelector('[data-hero-card-play-pause]');
  if (!centralBtn) return;
  const safeLabel = (label || 'Video').trim();
  centralBtn.setAttribute('aria-label', `Play ${safeLabel} video`);
}

function prepareRteVideo(video) {
  video.removeAttribute('controls');
  video.setAttribute('playsinline', '');
  video.setAttribute('autoplay', '');
  video.setAttribute('loop', '');
  video.setAttribute('muted', '');
  video.setAttribute('data-hero-card-video', '');
  video.setAttribute('tabindex', '-1');
  if (!video.hasAttribute('preload')) {
    video.setAttribute('preload', 'metadata');
  }
}

function injectControlsFromTemplate(wrapper, ariaLabel) {
  const template = document.getElementById('custom-video-controls-template');
  if (!template || !template.content) return false;

  const fragment = template.content.cloneNode(true);
  const overlay = fragment.querySelector('.banner-video-play-pause');
  const bar = fragment.querySelector('.controls');

  if (overlay) wrapper.insertBefore(overlay, wrapper.firstChild);
  if (bar) wrapper.appendChild(bar);

  setCentralPlayAriaLabel(wrapper, ariaLabel);
  return !!(overlay || bar);
}

function enhanceVideo(video) {
  if (!shouldEnhanceVideo(video)) return null;

  const ariaLabel = getVideoAriaLabel(video);
  const wrapper = document.createElement('custom-video');
  wrapper.className = 'custom-video';
  wrapper.setAttribute('data-video-wrapper', '');
  wrapper.setAttribute('data-enhanced', 'true');

  const parent = video.parentNode;
  if (!parent) return null;

  parent.insertBefore(wrapper, video);
  prepareRteVideo(video);
  wrapper.appendChild(video);

  if (!injectControlsFromTemplate(wrapper, ariaLabel)) {
    parent.insertBefore(video, wrapper);
    wrapper.remove();
    return null;
  }

  return wrapper;
}

function enhanceRteVideos(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const roots =
    root && root.matches && root.matches(RTE_VIDEO_ROOT_SELECTOR)
      ? [root]
      : Array.from(scope.querySelectorAll(RTE_VIDEO_ROOT_SELECTOR));

  const videos = [];
  roots.forEach((rteRoot) => {
    rteRoot.querySelectorAll('video').forEach((video) => {
      if (shouldEnhanceVideo(video)) videos.push(video);
    });
  });

  return videos.map((video) => enhanceVideo(video)).filter(Boolean);
}

function initRteVideoEnhancement() {
  enhanceRteVideos(document);
}

window.CustomVideoEnhance = {
  shouldEnhanceVideo,
  enhanceVideo,
  enhanceRteVideos,
  init: initRteVideoEnhancement,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRteVideoEnhancement);
} else {
  initRteVideoEnhancement();
}
